import React, { useEffect, useState, useCallback } from "react";
import { Star, Plus, X, CloudSun, MapPin, Cake, ExternalLink, Loader2, Search, Navigation, Pencil, Clock, PanelRightClose, Calculator as CalcIcon, Percent, ArrowRightLeft, Coins, Delete, FolderPlus, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { saintOfDay } from "../lib/saints";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const WMAP = {
  0: ["Ensoleillé", "☀️"], 1: ["Plutôt clair", "🌤️"], 2: ["Partiellement nuageux", "⛅"], 3: ["Couvert", "☁️"],
  45: ["Brouillard", "🌫️"], 48: ["Brouillard givrant", "🌫️"], 51: ["Bruine légère", "🌦️"], 53: ["Bruine", "🌦️"], 55: ["Bruine dense", "🌧️"],
  61: ["Pluie faible", "🌧️"], 63: ["Pluie", "🌧️"], 65: ["Forte pluie", "🌧️"], 71: ["Neige faible", "🌨️"], 73: ["Neige", "🌨️"], 75: ["Forte neige", "❄️"],
  80: ["Averses", "🌦️"], 81: ["Averses", "🌧️"], 82: ["Fortes averses", "⛈️"], 95: ["Orage", "⛈️"], 96: ["Orage grêle", "⛈️"], 99: ["Orage violent", "⛈️"],
};

function round2(v) { return Math.round((v + Number.EPSILON) * 100) / 100; }
function fmt(v) { return isFinite(v) ? v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"; }

function ToolsWidget() {
  const [tool, setTool] = useState("calc");
  const tabs = [["calc", "Calc", CalcIcon], ["vat", "HT/TTC", Percent], ["fx", "€ / $", Coins]];
  return (
    <div className="p-5 border-b border-white/[0.06]" data-testid="tools-widget">
      <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-2.5 flex items-center gap-1.5"><Wrench size={12} /> Outils</p>
      <div className="flex gap-1 p-1 rounded-xl bg-white/[0.05] border border-white/10 mb-3">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} data-testid={`tool-tab-${k}`} onClick={() => setTool(k)} className={`flex-1 flex items-center justify-center gap-1 h-7 rounded-lg text-[11px] font-semibold transition-colors ${tool === k ? "bg-sky-400 text-zinc-950" : "text-zinc-400 hover:text-white"}`}><Icon size={12} /> {label}</button>
        ))}
      </div>
      {tool === "calc" && <CalcTool />}
      {tool === "vat" && <VatTool />}
      {tool === "fx" && <FxTool />}
    </div>
  );
}

function CalcTool() {
  const [expr, setExpr] = useState("");
  const [res, setRes] = useState("");
  const press = (k) => {
    if (k === "C") { setExpr(""); setRes(""); return; }
    if (k === "⌫") { setExpr((e) => e.slice(0, -1)); return; }
    if (k === "=") {
      try {
        if (!/^[0-9+\-*/.()%\s]+$/.test(expr) || !expr) throw 0;
        const safe = expr.replace(/%/g, "/100");
        // eslint-disable-next-line no-new-func
        const v = Function(`"use strict";return (${safe})`)();
        setRes(typeof v === "number" && isFinite(v) ? String(round2(v)) : "Erreur");
      } catch { setRes("Erreur"); }
      return;
    }
    setExpr((e) => e + k);
  };
  const keys = ["C", "(", ")", "⌫", "7", "8", "9", "/", "4", "5", "6", "*", "1", "2", "3", "-", "0", ".", "%", "+"];
  return (
    <div data-testid="tool-calc">
      <div className="rounded-xl bg-background/50 border border-white/10 px-3 py-2 mb-2 text-right">
        <div className="text-xs text-zinc-400 truncate min-h-4">{expr || "0"}</div>
        <div className="font-cabinet font-black text-2xl text-sky-300 tabular-nums truncate">{res || "0"}</div>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {keys.map((k) => (
          <button key={k} data-testid={`calc-${k}`} onClick={() => press(k)} className={`h-8 rounded-lg text-sm font-semibold transition-colors ${k === "C" ? "bg-signal/20 text-signal" : "= ⌫".includes(k) ? "bg-white/[0.08] text-zinc-200" : "+-*/".includes(k) || k === "%" ? "bg-sky-400/15 text-sky-300" : "bg-white/[0.04] text-zinc-200 hover:bg-white/[0.1]"}`}>{k}</button>
        ))}
        <button data-testid="calc-=" onClick={() => press("=")} className="col-span-4 h-8 rounded-lg bg-sky-400 text-zinc-950 text-sm font-bold hover:brightness-110">=</button>
      </div>
    </div>
  );
}

function VatTool() {
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState("20");
  const [fromHt, setFromHt] = useState(true);
  const a = parseFloat(amount) || 0; const r = parseFloat(rate) || 0;
  const ht = fromHt ? a : a / (1 + r / 100);
  const ttc = fromHt ? a * (1 + r / 100) : a;
  const tva = ttc - ht;
  return (
    <div data-testid="tool-vat" className="space-y-2">
      <div className="flex gap-1.5">
        <input data-testid="vat-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Montant" className="flex-1 min-w-0 bg-background/50 border border-white/10 rounded-lg px-2.5 h-9 text-sm text-white focus:outline-none focus:border-sky-400/60" />
        <div className="flex items-center gap-1 bg-background/50 border border-white/10 rounded-lg px-2 h-9 w-20">
          <input data-testid="vat-rate" type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="w-full bg-transparent text-sm text-white text-right focus:outline-none" /><span className="text-xs text-zinc-500">%</span>
        </div>
      </div>
      <button data-testid="vat-toggle" onClick={() => setFromHt((v) => !v)} className="w-full flex items-center justify-center gap-2 h-8 rounded-lg bg-white/[0.05] border border-white/10 text-xs font-medium text-zinc-300 hover:text-white"><ArrowRightLeft size={13} className="text-sky-300" /> Saisie en {fromHt ? "HT" : "TTC"}</button>
      <div className="grid grid-cols-3 gap-1.5 text-center">
        <div className="rounded-lg bg-background/50 border border-white/10 py-2"><div className="text-[10px] text-zinc-500">HT</div><div className="font-cabinet font-bold text-sm text-white tabular-nums">{fmt(ht)}</div></div>
        <div className="rounded-lg bg-sky-400/10 border border-sky-400/20 py-2"><div className="text-[10px] text-sky-300">TVA</div><div className="font-cabinet font-bold text-sm text-sky-300 tabular-nums">{fmt(tva)}</div></div>
        <div className="rounded-lg bg-background/50 border border-white/10 py-2"><div className="text-[10px] text-zinc-500">TTC</div><div className="font-cabinet font-bold text-sm text-white tabular-nums">{fmt(ttc)}</div></div>
      </div>
    </div>
  );
}

function FxTool() {
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState(1.08);
  const [eurToUsd, setEurToUsd] = useState(true);
  const [live, setLive] = useState(false);
  useEffect(() => {
    fetch("https://api.frankfurter.app/latest?from=EUR&to=USD").then((r) => r.json()).then((d) => { if (d?.rates?.USD) { setRate(d.rates.USD); setLive(true); } }).catch(() => {});
  }, []);
  const a = parseFloat(amount) || 0; const r = parseFloat(rate) || 0;
  const out = eurToUsd ? a * r : (r ? a / r : 0);
  return (
    <div data-testid="tool-fx" className="space-y-2">
      <div className="flex gap-1.5">
        <input data-testid="fx-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={eurToUsd ? "Montant €" : "Montant $"} className="flex-1 min-w-0 bg-background/50 border border-white/10 rounded-lg px-2.5 h-9 text-sm text-white focus:outline-none focus:border-sky-400/60" />
        <button data-testid="fx-toggle" onClick={() => setEurToUsd((v) => !v)} className="shrink-0 flex items-center gap-1 h-9 px-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-xs font-semibold text-zinc-200 hover:text-white"><ArrowRightLeft size={13} className="text-sky-300" /> {eurToUsd ? "€→$" : "$→€"}</button>
      </div>
      <div className="rounded-lg bg-sky-400/10 border border-sky-400/20 px-3 py-2.5 flex items-baseline justify-between">
        <span className="text-xs text-sky-300">{eurToUsd ? "$ US" : "€ EUR"}</span>
        <span className="font-cabinet font-black text-xl text-sky-300 tabular-nums">{fmt(out)}</span>
      </div>
      <div className="flex items-center justify-between text-[11px] text-zinc-500">
        <span>1 € = <input data-testid="fx-rate" type="number" step="0.001" value={rate} onChange={(e) => { setRate(e.target.value); setLive(false); }} className="w-14 bg-transparent border-b border-white/10 text-zinc-300 text-center focus:outline-none" /> $</span>
        {live && <span className="text-sky-300/70">● taux live</span>}
      </div>
    </div>
  );
}

export default function RightRail() {
  const { user, setUser } = useAuth();
  const { selectedId } = useCompany();
  const scope = selectedId || "all";
  const [now, setNow] = useState(new Date());
  const [open, setOpen] = useState(() => localStorage.getItem("fd_rightpanel") !== "0");
  const [adding, setAdding] = useState(null);
  const [weather, setWeather] = useState(null);
  const [wLoading, setWLoading] = useState(false);
  const [cityInput, setCityInput] = useState("");
  const [editingCity, setEditingCity] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const togglePanel = () => setOpen((o) => { localStorage.setItem("fd_rightpanel", o ? "0" : "1"); return !o; });

  const prefs = user?.prefs || {};
  const favMap = prefs.favorites || {};
  const favs = favMap[scope] || [];
  const city = prefs.weather_city || "";

  const savePrefs = async (patch) => { const { data } = await api.patch("/profile", { prefs: { ...prefs, ...patch } }); setUser(data); return data; };
  const addFav = async (fav) => { const next = { ...favMap, [scope]: [...favs, { id: Date.now().toString(), ...fav }] }; await savePrefs({ favorites: next }); setAdding(null); };
  const removeFav = async (id) => { const next = { ...favMap, [scope]: favs.filter((f) => f.id !== id) }; await savePrefs({ favorites: next }); };

  const loadWeather = useCallback(async (q) => {
    if (!q) return;
    setWLoading(true);
    try {
      const geo = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=1&language=fr&format=json`).then((r) => r.json());
      const place = geo.results?.[0];
      if (!place) { setWeather({ error: "Ville introuvable" }); return; }
      const wx = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${place.latitude}&longitude=${place.longitude}&current=temperature_2m,weather_code&daily=temperature_2m_max,temperature_2m_min&timezone=auto`).then((r) => r.json());
      setWeather({ name: place.name, temp: Math.round(wx.current.temperature_2m), code: wx.current.weather_code, max: Math.round(wx.daily.temperature_2m_max[0]), min: Math.round(wx.daily.temperature_2m_min[0]) });
    } catch { setWeather({ error: "Météo indisponible" }); }
    finally { setWLoading(false); }
  }, []);

  useEffect(() => { if (city) loadWeather(city); }, [city, loadWeather]);

  const setCity = async () => { if (!cityInput.trim()) return; await savePrefs({ weather_city: cityInput.trim() }); setCityInput(""); setEditingCity(false); };

  const useGeolocation = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non disponible"); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const rev = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=fr`).then((r) => r.json());
        const name = rev.city || rev.locality || rev.principalSubdivision;
        if (!name) { toast.error("Localisation introuvable"); return; }
        await savePrefs({ weather_city: name });
        setEditingCity(false);
        toast.success(`Météo de ${name}`);
      } catch { toast.error("Localisation échouée"); }
      finally { setGeoLoading(false); }
    }, () => { setGeoLoading(false); toast.error("Accès à la position refusé"); }, { timeout: 8000 });
  };

  const wInfo = weather && weather.code !== undefined ? (WMAP[weather.code] || ["—", "🌡️"]) : null;

  return (
    <aside className="hidden xl:flex shrink-0" data-testid="right-rail">
      {/* Slim icon rail */}
      <div className="w-14 flex flex-col items-center py-4 gap-1.5">
        <button data-testid="right-widget-btn-toggle" onClick={togglePanel} title={open ? "Masquer le panneau" : "Afficher le panneau"} className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${open ? "bg-klein/20 text-klein" : "text-zinc-400 hover:bg-white/[0.08] hover:text-white"}`}>
          <PanelRightClose size={18} className={open ? "" : "rotate-180"} />
        </button>
        <div className="w-7 h-px bg-white/10 my-1" />
        <button data-testid="right-widget-btn-clock" onClick={() => setOpen(true)} title="Horloge" className="w-10 h-10 rounded-xl flex items-center justify-center text-zinc-300 hover:bg-white/[0.08] hover:text-white transition-colors"><Clock size={18} /></button>
        <button data-testid="right-widget-btn-weather" onClick={() => setOpen(true)} title="Météo" className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/[0.08] transition-colors text-lg leading-none">{wInfo ? wInfo[1] : <CloudSun size={18} className="text-zinc-300" />}</button>
        <button data-testid="right-widget-btn-favorites" onClick={() => setOpen(true)} title="Favoris" className="w-10 h-10 rounded-xl flex items-center justify-center text-zinc-300 hover:bg-white/[0.08] hover:text-white transition-colors relative"><Star size={18} />{favs.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-klein text-zinc-950 text-[9px] font-bold flex items-center justify-center">{favs.length}</span>}</button>
        <div className="mt-auto text-[10px] font-cabinet font-bold text-zinc-500 tabular-nums [writing-mode:vertical-rl] rotate-180">{now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
      </div>

      {/* Slide-out panel */}
      <div className={`transition-all duration-300 overflow-hidden ${open ? "w-[276px]" : "w-0"}`}>
        <div className="w-[276px] h-full overflow-y-auto">
          {/* Clock + date + saint */}
          <div className="p-5 border-b border-white/[0.06]">
            <div className="font-cabinet font-black text-4xl tabular-nums text-white tracking-tight">{now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}<span className="text-lg text-zinc-400">:{now.toLocaleTimeString("fr-FR", { second: "2-digit" }).padStart(2, "0")}</span></div>
            <div className="text-sm text-zinc-300 capitalize mt-1">{now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
            <div className="mt-3 flex items-center gap-2 text-xs text-klein bg-klein/10 rounded-lg px-2.5 py-1.5 w-fit"><Cake size={13} /> Fête : {saintOfDay(now)}</div>
          </div>

          {/* Weather */}
          <div className="p-5 border-b border-white/[0.06]">
            <div className="flex items-center justify-between mb-2.5">
              <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><CloudSun size={12} /> Météo</p>
              {city && !editingCity && <button data-testid="weather-edit" onClick={() => { setEditingCity(true); setCityInput(city); }} className="text-zinc-500 hover:text-klein transition-colors"><Pencil size={12} /></button>}
            </div>

            {(!city || editingCity) ? (
              <div className="space-y-2.5" data-testid="weather-city-form">
                <div className="group relative flex items-center rounded-2xl bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 focus-within:border-klein/60 focus-within:from-klein/10 focus-within:shadow-[0_0_0_3px_rgba(59,130,246,0.12)] transition-all px-3.5 h-11">
                  <Search size={15} className="text-zinc-500 group-focus-within:text-klein transition-colors shrink-0" />
                  <input data-testid="weather-city-input" value={cityInput} onChange={(e) => setCityInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setCity()} placeholder="Votre ville…" className="flex-1 bg-transparent text-sm text-white px-2.5 focus:outline-none placeholder:text-zinc-500" />
                  {cityInput.trim() && <button data-testid="weather-set" onClick={setCity} className="shrink-0 h-7 px-3 rounded-xl bg-klein text-zinc-950 text-xs font-semibold hover:brightness-110 transition">OK</button>}
                </div>
                <button data-testid="weather-geo" onClick={useGeolocation} disabled={geoLoading} className="w-full flex items-center justify-center gap-2 h-9 rounded-2xl bg-white/[0.04] border border-white/10 text-xs font-medium text-zinc-300 hover:text-white hover:border-klein/40 hover:bg-klein/5 transition-all disabled:opacity-60">
                  {geoLoading ? <Loader2 size={13} className="animate-spin" /> : <Navigation size={13} className="text-klein" />}
                  {geoLoading ? "Localisation…" : "Utiliser ma position"}
                </button>
              </div>
            ) : wLoading ? (
              <div className="flex items-center gap-2 text-zinc-400 text-sm"><Loader2 size={14} className="animate-spin" /> Chargement…</div>
            ) : wInfo ? (
              <div className="rounded-2xl bg-gradient-to-br from-klein/15 to-transparent border border-klein/20 p-3.5 flex items-center gap-3.5" data-testid="weather-card">
                <div className="text-[42px] leading-none">{wInfo[1]}</div>
                <div className="min-w-0">
                  <div className="font-cabinet font-black text-3xl text-white tabular-nums leading-none">{weather.temp}°</div>
                  <div className="text-xs text-zinc-200 mt-1">{wInfo[0]}</div>
                  <div className="text-[11px] text-zinc-400 flex items-center gap-1 mt-0.5 truncate"><MapPin size={10} className="shrink-0 text-klein" /> {weather.name} · {weather.min}°/{weather.max}°</div>
                </div>
              </div>
            ) : (
              <div className="space-y-2.5">
                <p className="text-xs text-signal">{weather?.error || "Ville introuvable."}</p>
                <button data-testid="weather-retry" onClick={() => { setEditingCity(true); setCityInput(city); }} className="text-xs text-klein hover:underline">Changer de ville</button>
              </div>
            )}
          </div>

          {/* Tools: calculator + converters */}
          <ToolsWidget />

          {/* Favorites per company, grouped */}
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Star size={12} /> Raccourcis</p>
              <button data-testid="fav-add" onClick={() => setAdding({ label: "", url: "", color: "#38BDF8", group: "Général" })} className="p-1 rounded-md text-zinc-400 hover:text-sky-300 hover:bg-white/[0.06]"><Plus size={15} /></button>
            </div>
            {favs.length === 0 ? <p className="text-xs text-zinc-500">Aucun raccourci. Ajoutez vos outils par groupes.</p> : (
              (() => {
                const groups = [];
                const idx = {};
                favs.forEach((f) => { const g = f.group || "Général"; if (idx[g] === undefined) { idx[g] = groups.length; groups.push({ g, items: [] }); } groups[idx[g]].items.push(f); });
                return (
                  <div className="space-y-3">
                    {groups.map((grp) => (
                      <div key={grp.g} data-testid={`fav-group-${grp.g}`}>
                        <div className="flex items-center gap-1.5 mb-1.5 px-0.5"><FolderPlus size={11} className="text-zinc-500" /><span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide truncate">{grp.g}</span><span className="text-[10px] text-zinc-600">{grp.items.length}</span></div>
                        <div className="space-y-1.5">
                          {grp.items.map((f) => (
                            <div key={f.id} className="group flex items-center gap-2.5 rounded-xl bg-background/40 border border-white/10 px-3 py-2.5 hover:border-sky-400/40 transition-colors" data-testid={`fav-${f.id}`}>
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: f.color }} />
                              <a href={f.url.startsWith("http") ? f.url : `https://${f.url}`} target="_blank" rel="noreferrer" className="flex-1 min-w-0 flex items-center gap-1.5 text-sm text-zinc-200 hover:text-white truncate"><span className="truncate">{f.label}</span><ExternalLink size={11} className="text-zinc-500 shrink-0" /></a>
                              <button onClick={() => removeFav(f.id)} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-signal transition-opacity"><X size={14} /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!adding} onOpenChange={(o) => !o && setAdding(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><Star size={18} className="text-klein" /> Nouveau favori</DialogTitle><DialogDescription className="sr-only">Ajouter un raccourci vers un outil externe</DialogDescription></DialogHeader>
          {adding && (
            <div className="space-y-3">
              <input data-testid="fav-label" value={adding.label} onChange={(e) => setAdding({ ...adding, label: e.target.value })} placeholder="Nom de l'outil (ex : Stripe)" className="w-full bg-background border border-white/10 rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400/20" />
              <input data-testid="fav-url" value={adding.url} onChange={(e) => setAdding({ ...adding, url: e.target.value })} placeholder="https://…" className="w-full bg-background border border-white/10 rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400/20" />
              <div>
                <input data-testid="fav-group" list="fav-groups" value={adding.group} onChange={(e) => setAdding({ ...adding, group: e.target.value })} placeholder="Groupe (ex : Compta, Outils, Réseaux)" className="w-full bg-background border border-white/10 rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400/20" />
                <datalist id="fav-groups">{[...new Set([...(favs.map((f) => f.group).filter(Boolean)), "Général", "Compta", "Outils", "Réseaux"])].map((g, i) => <option key={i} value={g} />)}</datalist>
              </div>
              <div className="flex items-center gap-2">
                {["#38BDF8", "#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#FF3B3B"].map((c) => (
                  <button key={c} onClick={() => setAdding({ ...adding, color: c })} className={`w-7 h-7 rounded-full border-2 ${adding.color === c ? "border-white" : "border-transparent"}`} style={{ background: c }} />
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setAdding(null)} className="h-9 px-4 rounded-lg border border-white/10 text-sm">Annuler</button>
            <button data-testid="fav-save" onClick={() => adding.label && adding.url && addFav(adding)} className="h-9 px-4 rounded-lg bg-sky-400 text-zinc-950 text-sm font-semibold">Ajouter</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
